import React, { useState } from "react";
import FormField from "../forms/FormField";
import LoadingSpinner from "../common/LoadingSpinner";
import {
  createCategory,
  updateCategory,
  deleteCategory,
} from "../../services/itemService";
import { GROCERY_CATEGORIES, SUCCESS_MESSAGES } from "../../utils/constants";

const CategoryManager = ({
  categories,
  onCategoryUpdate,
  onSuccess,
  onError,
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    icon: "📦",
    isActive: true,
  });

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      onError("Category name is required");
      return;
    }

    try {
      setLoading(true);

      if (editingCategory) {
        await updateCategory(editingCategory.id, formData);
        onSuccess(SUCCESS_MESSAGES.CATEGORY_UPDATED);
      } else {
        await createCategory(formData);
        onSuccess(SUCCESS_MESSAGES.CATEGORY_CREATED);
      }

      resetForm();
      onCategoryUpdate();
    } catch (error) {
      onError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || "",
      icon: category.icon || "📦",
      isActive: category.isActive !== false,
    });
    setShowForm(true);
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm("Are you sure you want to delete this category?")) {
      return;
    }

    try {
      setLoading(true);
      await deleteCategory(categoryId);
      onSuccess(SUCCESS_MESSAGES.CATEGORY_DELETED);
      onCategoryUpdate();
    } catch (error) {
      onError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      icon: "📦",
      isActive: true,
    });
    setEditingCategory(null);
    setShowForm(false);
  };

  const suggestCategory = (categoryName) => {
    setFormData((prev) => ({ ...prev, name: categoryName }));
    setShowForm(true);
  };

  return (
    <div className="category-manager">
      {/* Header */}
      <div className="section-header">
        <h2>Category Management</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          className="btn btn-primary"
          disabled={loading}
        >
          {showForm ? "Cancel" : "+ Add Category"}
        </button>
      </div>

      {/* Quick Add Suggestions */}
      {!showForm && categories.length === 0 && (
        <div className="quick-suggestions">
          <h3>Quick Start - Add Common Categories:</h3>
          <div className="suggestion-grid">
            {GROCERY_CATEGORIES.slice(0, 8).map((category) => (
              <button
                key={category}
                onClick={() => suggestCategory(category)}
                className="suggestion-button"
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Category Form */}
      {showForm && (
        <div className="form-section">
          <h3>{editingCategory ? "Edit Category" : "Add New Category"}</h3>
          <form onSubmit={handleSubmit} className="category-form">
            <div className="form-row">
              <FormField
                label="Category Name"
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="e.g., Fruits & Vegetables"
                required
              />
              <FormField
                label="Icon"
                type="text"
                name="icon"
                value={formData.icon}
                onChange={(e) => handleInputChange("icon", e.target.value)}
                placeholder="📦"
                maxLength={2}
              />
            </div>

            <FormField
              label="Description"
              type="textarea"
              name="description"
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Brief description of this category"
              rows={3}
            />

            <FormField
              label="Status"
              type="select"
              name="isActive"
              value={formData.isActive.toString()}
              onChange={(e) =>
                handleInputChange("isActive", e.target.value === "true")
              }
              options={[
                { value: "true", label: "Active" },
                { value: "false", label: "Inactive" },
              ]}
            />

            <div className="form-actions">
              <button
                type="button"
                onClick={resetForm}
                className="btn btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <LoadingSpinner size="small" />
                ) : editingCategory ? (
                  "Update Category"
                ) : (
                  "Create Category"
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Categories List */}
      <div className="categories-list">
        {categories.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📁</div>
            <h3>No Categories Yet</h3>
            <p>Create your first category to start organizing items</p>
          </div>
        ) : (
          <div className="categories-grid">
            {categories.map((category) => (
              <div key={category.id} className="category-card">
                <div className="category-header">
                  <span className="category-icon">{category.icon || "📦"}</span>
                  <div className="category-info">
                    <h4>{category.name}</h4>
                    {category.description && (
                      <p className="category-description">
                        {category.description}
                      </p>
                    )}
                  </div>
                  <div className="category-status">
                    <span
                      className={`status-badge ${category.isActive !== false ? "active" : "inactive"}`}
                    >
                      {category.isActive !== false ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>

                <div className="category-actions">
                  <button
                    onClick={() => handleEdit(category)}
                    className="btn btn-sm btn-secondary"
                    disabled={loading}
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="btn btn-sm btn-danger"
                    disabled={loading}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryManager;
